// Hotel-in-{destination} SEO landing page.
// Generates dynamic <title>, meta description, JSON-LD TouristDestination + ItemList
// schema for first-page hotels, and includes a server-rendered list of top
// suggestions plus a deep-link search CTA.
//
// Route slug: `/hotels-in/:slug` where slug ∈ {'mumbai', 'goa', 'dubai', 'bali', 'singapore', ...}
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Star, MapPin, ChevronRight } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';
import NoImagePlaceholder from './NoImagePlaceholder';

const DEST_META = {
  'mumbai':    { city: 'Mumbai',    country: 'India',     hero: 'https://images.unsplash.com/photo-1567157577867-05ccb1388e66?w=1600', blurb: 'Bollywood capital and India\'s financial powerhouse.' },
  'goa':       { city: 'Goa',       country: 'India',     hero: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1600', blurb: 'Sun, sand, and 100+ beaches along the Konkan coast.' },
  'delhi':     { city: 'Delhi',     country: 'India',     hero: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=1600', blurb: 'India\'s historic capital — Mughal monuments and modern markets.' },
  'bengaluru': { city: 'Bengaluru', country: 'India',     hero: 'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=1600', blurb: 'Garden city + tech capital — perfect base for South India trips.' },
  'jaipur':    { city: 'Jaipur',    country: 'India',     hero: 'https://images.unsplash.com/photo-1599661046289-e31897846e41?w=1600', blurb: 'The Pink City — Rajasthani heritage and palace hotels.' },
  'dubai':     { city: 'Dubai',     country: 'UAE',       hero: 'https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1600', blurb: 'Burj Khalifa, desert safaris, and luxury beach resorts.' },
  'bali':      { city: 'Bali',      country: 'Indonesia', hero: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1600', blurb: 'Tropical paradise — rice terraces, beaches and wellness retreats.' },
  'singapore': { city: 'Singapore', country: 'Singapore', hero: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1600', blurb: 'Garden city, futuristic skylines, and world-class food.' },
};

const today = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const apiBase = process.env.REACT_APP_BACKEND_URL || '';

const HotelDestinationSEO = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = DEST_META[slug] || { city: (slug || 'India').replace(/-/g, ' '), country: 'India',
    hero: 'https://images.unsplash.com/photo-1564507592333-c60657eea523?w=1600', blurb: 'Hand-picked properties for your next trip.' };

  // Lightweight SEO: per-route title + meta + canonical + JSON-LD
  useEffect(() => {
    const title = `Hotels in ${meta.city} · Best Deals 2026 · Elite Travo`;
    const desc  = `Compare hotels in ${meta.city}, ${meta.country}. Live prices, instant booking, free cancellation. ${meta.blurb}`;
    document.title = title;
    const d = document.querySelector('meta[name="description"]'); if (d) d.setAttribute('content', desc);
    const canonical = document.querySelector('link[rel="canonical"]') || (() => { const l = document.createElement('link'); l.rel = 'canonical'; document.head.appendChild(l); return l; })();
    canonical.href = `${window.location.origin}/hotels-in/${slug}`;

    // JSON-LD: TouristDestination + ItemList of top hotels (rebuilt on hotel load)
    let ld = document.getElementById('seo-jsonld');
    if (!ld) { ld = document.createElement('script'); ld.id = 'seo-jsonld'; ld.type = 'application/ld+json'; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'TouristDestination',
      name: `Hotels in ${meta.city}`,
      address: { '@type': 'PostalAddress', addressLocality: meta.city, addressCountry: meta.country },
      description: desc,
      image: meta.hero,
      includesAttraction: hotels.slice(0, 8).map((h) => ({
        '@type': 'Hotel',
        name: h.name,
        starRating: h.rating ? { '@type': 'Rating', ratingValue: h.rating } : undefined,
        priceRange: '₹₹₹',
        address: { '@type': 'PostalAddress', addressLocality: h.city || meta.city },
      })),
    });
    return () => { /* keep tags for back-button view-source */ };
  }, [slug, meta.city, meta.country, meta.blurb, meta.hero, hotels]);

  useEffect(() => {
    axios.post(`${apiBase}/api/v2/hotels/search`, {
      city: meta.city, check_in: today(7), check_out: today(9),
      rooms: 1, guests: 2, nights: 2, tier: 'b2c',
    }).then((r) => setHotels((r.data.hotels || []).slice(0, 12)))
      .catch(() => setHotels([]))
      .finally(() => setLoading(false));
  }, [meta.city]);

  return (
    <div className="bg-white dark:bg-gray-950 min-h-screen">
      <div className="relative h-[280px] sm:h-[360px] bg-cover bg-center" style={{ backgroundImage: `url(${meta.hero})` }}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/70" />
        <div className="relative h-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-8">
          <p className="text-white/80 text-xs uppercase tracking-widest">Top destination</p>
          <h1 className="text-3xl sm:text-5xl font-bold text-white mt-1" data-testid="seo-h1">Hotels in {meta.city}</h1>
          <p className="text-white/90 text-sm sm:text-base mt-2 max-w-2xl">{meta.blurb}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Top {meta.city} hotels right now</h2>
          <Button onClick={() => navigate(`/hotels/search?city=${encodeURIComponent(meta.city)}&check_in=${today(7)}&check_out=${today(9)}&rooms=1&guests=2`)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="seo-cta-search">
            See all hotels →
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-48 bg-gray-100 dark:bg-gray-900 rounded-xl shimmer" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="seo-hotels">
            {hotels.map((h) => (
              <Link key={h.id} to={`/hotels/${h.id}`} state={{ item: h }} className="block">
                <Card className="hover:shadow-md transition-all overflow-hidden h-full">
                  <div className="h-36 bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    {h.image ? (
                      <img src={h.image} alt={h.name} loading="lazy" className="w-full h-full object-cover" />
                    ) : (
                      <NoImagePlaceholder />
                    )}
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white truncate">{h.name}</h3>
                    <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{h.area || h.city}</p>
                    <div className="flex items-center justify-between mt-2">
                      {h.rating > 0 && <div className="flex items-center gap-1 text-xs"><Star className="h-3 w-3 text-yellow-500 fill-current" />{h.rating}</div>}
                      <p className="font-bold text-red-600 text-sm">{formatPrice(h.display_price ?? h.price)}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Internal-link footer — small text block for SEO crawlers */}
        <div className="mt-10 border-t border-gray-200 dark:border-gray-800 pt-6">
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-3">Explore other destinations</h3>
          <div className="flex flex-wrap gap-2 text-sm">
            {Object.entries(DEST_META).filter(([s]) => s !== slug).map(([s, m]) => (
              <Link key={s} to={`/hotels-in/${s}`} className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-900 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-700 dark:text-gray-300 hover:text-red-600 transition" data-testid={`seo-internal-${s}`}>
                Hotels in {m.city} <ChevronRight className="h-3 w-3 inline" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelDestinationSEO;
